import { MAX_DECODED_PIXELS, MAX_VARIANT_PIXELS } from './constants';
import { processForOcr, type PrepMeta, type VariantId } from './imageOps';
import { OcrError } from './types';

export interface CropRect {
  /** Normalised 0-1 values relative to the decoded source image. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export type DecodedSource = ImageBitmap | HTMLImageElement | HTMLCanvasElement;

export async function decodeImageFile(file: Blob): Promise<DecodedSource> {
  if (typeof createImageBitmap === 'function') {
    try {
      // imageOrientation: 'from-image' honours EXIF orientation.
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    } catch (e) {
      /* fall through to <img> decoding */
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new OcrError('DECODE_FAILED', 'This image could not be decoded by your browser.'));
    };
    img.src = url;
  });
}

export function sourceSize(source: DecodedSource): { width: number; height: number } {
  const anySource = source as HTMLImageElement;
  const width = (anySource.naturalWidth || (source as ImageBitmap).width) as number;
  const height = (anySource.naturalHeight || (source as ImageBitmap).height) as number;
  return { width, height };
}

export interface PrepareOptions {
  /** 0 / 90 / 180 / 270, user selected. */
  rotationDeg?: number;
  crop?: CropRect | null;
  variant?: VariantId;
  /** Long-side target used when upscaling genuinely low-resolution text. */
  upscaleTarget?: number;
  darkThreshold?: number;
}

export interface PreparedCanvas {
  canvas: HTMLCanvasElement;
  meta: PrepMeta;
  /** Factor applied to the source pixels; needed to map boxes back to originals. */
  scale: number;
  sourceWidth: number;
  sourceHeight: number;
}

/**
 * Computes the scale factor used for an OCR canvas: proportional downscale for
 * very large images, gentle upscale for genuinely small text, capped for memory.
 */
export function computeScale(sw: number, sh: number, upscaleTarget: number): number {
  const pixels = sw * sh;
  let scale = 1;
  if (pixels > MAX_DECODED_PIXELS) {
    scale = Math.sqrt(MAX_DECODED_PIXELS / pixels);
  } else {
    const longSide = Math.max(sw, sh);
    if (longSide < upscaleTarget) scale = Math.min(3, upscaleTarget / longSide);
  }
  if (pixels * scale * scale > MAX_VARIANT_PIXELS) {
    scale = Math.sqrt(MAX_VARIANT_PIXELS / pixels);
  }
  return scale;
}

/**
 * Builds a fresh temporary OCR canvas. The displayed preview is never touched.
 */
export function prepareOcrCanvas(source: DecodedSource, options: PrepareOptions = {}): PreparedCanvas {
  const { rotationDeg = 0, crop = null, variant = 'upscaled', upscaleTarget = 1800, darkThreshold } = options;
  const { width: srcW, height: srcH } = sourceSize(source);
  if (!srcW || !srcH) {
    throw new OcrError('EMPTY_IMAGE', 'This image decoded to zero width or height.');
  }

  const sx = crop ? Math.round(crop.x * srcW) : 0;
  const sy = crop ? Math.round(crop.y * srcH) : 0;
  const sw = crop ? Math.max(1, Math.round(crop.width * srcW)) : srcW;
  const sh = crop ? Math.max(1, Math.round(crop.height * srcH)) : srcH;

  const scale = computeScale(sw, sh, upscaleTarget);
  const drawW = Math.max(1, Math.round(sw * scale));
  const drawH = Math.max(1, Math.round(sh * scale));
  const rot = ((rotationDeg % 360) + 360) % 360;
  const swap = rot === 90 || rot === 270;

  const canvas = document.createElement('canvas');
  canvas.width = swap ? drawH : drawW;
  canvas.height = swap ? drawW : drawH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
  if (!ctx) throw new OcrError('DECODE_FAILED', 'Your browser could not create a drawing canvas.');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  if (rot) ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(source as CanvasImageSource, sx, sy, sw, sh, -drawW / 2, -drawH / 2, drawW, drawH);
  ctx.restore();

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const meta = processForOcr(imageData, { variant, darkThreshold });
  ctx.putImageData(imageData, 0, 0);

  return { canvas, meta, scale, sourceWidth: sw, sourceHeight: sh };
}

export function releaseCanvas(canvas: HTMLCanvasElement | null | undefined): void {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  ctx?.clearRect(0, 0, canvas.width, canvas.height);
  canvas.width = 1;
  canvas.height = 1;
}

export function releaseSource(source: DecodedSource | null | undefined): void {
  if (!source) return;
  const bitmap = source as ImageBitmap;
  if (typeof bitmap.close === 'function') bitmap.close();
}
